import {
  type ConnectorInfo,
  type WorkflowId,
  type WorkflowNodeId,
  type WorkflowNodeType,
} from '@blaze-shared/api-contracts-domain';
import { type BaseSchema } from '@blaze-shared/schema-domain';
import { type Workflow, type WorkflowNode } from '@blaze-shared/workflow-domain';

import {
  ColumnMissingWorkflowError,
  InputMissingWorkflowError,
  ParamMissingWorkflowError,
  TagMissingWorkflowError,
  type WorkflowError,
} from '../errors/workflow-error';
import {
  type WorkflowNodeColumnReference,
  type WorkflowNodeNamedInputReference,
  type WorkflowNodeParamReference,
  type WorkflowNodeTagReference,
} from '../references/references.models';
import { type SchemaWithValidationInfo } from '../validation-context/schema-with-validation-info';

export abstract class BaseWorkflowNode<T extends WorkflowNode> {
  get nodeId(): WorkflowNodeId {
    return this.node.id;
  }

  get nodeType(): WorkflowNodeType {
    return this.node.nodeType;
  }

  constructor(
    protected readonly node: T,
    public readonly workflowId: WorkflowId,
  ) {}

  abstract getColumnReferences(
    schemas: Map<string | symbol, BaseSchema>,
  ): WorkflowNodeColumnReference[];
  abstract getTagReferences(schemas: Map<string | symbol, BaseSchema>): WorkflowNodeTagReference[];
  abstract getParamReferences(
    schemas: Map<string | symbol, BaseSchema>,
    connectors: ConnectorInfo[],
  ): WorkflowNodeParamReference[];
  abstract getNamedInputReferences(
    schemas: Map<string | symbol, BaseSchema>,
  ): WorkflowNodeNamedInputReference[];

  validate(
    schemas: Map<string | symbol, SchemaWithValidationInfo>,
    connectors: ConnectorInfo[],
    _workflows: Workflow[] = [],
  ): WorkflowError<any>[] {
    const columnsReferences = this.getColumnReferences(schemas);
    const tagsReferences = this.getTagReferences(schemas);
    const paramReferences = this.getParamReferences(schemas, connectors);
    const namedInputsReferences = this.getNamedInputReferences?.(schemas);

    return [
      ...this.columnReferencesToWorkflowErrors(columnsReferences),
      ...this.tagReferencesToWorkflowErrors(tagsReferences),
      ...this.paramReferencesToWorkflowErrors(paramReferences),
      ...this.namedInputsReferencesToWorkflowErrors(namedInputsReferences),
    ].filter(Boolean);
  }

  private columnReferencesToWorkflowErrors(
    columnReferences: WorkflowNodeColumnReference[],
  ): WorkflowError<'COLUMN_MISSING_ERROR'>[] {
    return columnReferences
      .map((ref) => {
        if (!ref.schemaId) {
          return new ColumnMissingWorkflowError(
            this.workflowId,
            ref.columns,
            this.node.id,
            'datasetId' in this.node ? this.node.datasetId : undefined,
            ref.sourceId,
          );
        }
        return undefined;
      })
      .filter(Boolean) as WorkflowError<'COLUMN_MISSING_ERROR'>[];
  }

  private tagReferencesToWorkflowErrors(
    tagReferences: WorkflowNodeTagReference[],
  ): WorkflowError<'TAG_MISSING_ERROR'>[] {
    return tagReferences
      .map((ref) => {
        if (!ref.schemaId) {
          return new TagMissingWorkflowError(this.workflowId, ref.tag, this.node.id, ref.sourceId);
        }
        return undefined;
      })
      .filter(Boolean) as WorkflowError<any>[];
  }

  private paramReferencesToWorkflowErrors(
    paramReferences: WorkflowNodeParamReference[],
  ): WorkflowError<'PARAM_MISSING_ERROR'>[] {
    return paramReferences
      .map((ref) => {
        if (!ref.api && !ref.schemaId) {
          return new ParamMissingWorkflowError(
            this.workflowId,
            ref.param,
            this.node.id,
            ref.sourceId,
          );
        }
        return undefined;
      })
      .filter(Boolean) as WorkflowError<any>[];
  }

  private namedInputsReferencesToWorkflowErrors(
    namedInputsReferences: WorkflowNodeNamedInputReference[],
  ): WorkflowError<'INPUT_MISSING_ERROR'>[] {
    return namedInputsReferences
      .map((ref) => {
        if (!ref.schemaId) {
          return new InputMissingWorkflowError(
            this.workflowId,
            this.node.id,
            ref.namedInput,
            ref.sourceId,
          );
        }
        return undefined;
      })
      .filter(Boolean) as WorkflowError<any>[];
  }
}

export type NodeConstructor<T extends WorkflowNode> = new (
  node: T,
  workflowId: WorkflowId,
) => BaseWorkflowNode<T>;

export class NodeRegistry {
  private static readonly registry = new Map<WorkflowNodeType, NodeConstructor<any>>();

  static registerNode<T extends WorkflowNode>(
    nodeType: WorkflowNodeType,
    nodeClass: NodeConstructor<T>,
  ): void {
    NodeRegistry.registry.set(nodeType, nodeClass);
  }

  static getNode<T extends WorkflowNode>(nodeType: WorkflowNodeType): NodeConstructor<T> {
    const nodeClass = NodeRegistry.registry.get(nodeType);

    if (!nodeClass) {
      throw new Error(`No node registered for type "${nodeType}"`);
    }

    return nodeClass;
  }
}

export function WorkflowNode<T extends WorkflowNode>(
  nodeType: WorkflowNodeType,
): (nodeClass: NodeConstructor<T>) => void {
  return (nodeClass: NodeConstructor<T>): void => {
    NodeRegistry.registerNode(nodeType, nodeClass);
  };
}
